/**
 * Shared add-to-cart "extras" layer — window.EmbarkAddons.
 *
 * Any PDP feature that contributes extra line items, validation, or price to the
 * Add-to-Cart flow registers a small provider here. product-form.js then owns the
 * flow in one place:
 *   1. validateAll() — gate the submit (each provider validates its own DOM)
 *   2. addToCart()   — every provider's lineItems() in ONE combined /cart/add.js POST
 *   3. reflectTotal()— keep the total in the Add to Cart button in sync
 *   4. reflectSummary() — list what's in the build ("Your Build") from every provider
 *
 * Provider contract (all methods optional):
 *   {
 *     el?,                                       // for isConnected liveness check + line order
 *     lineItems?() -> [{ id, quantity, properties }],
 *     validate?()  -> { valid, invalid: [els], message? },
 *     subtotal?()  -> Number,                    // cents of this feature's selection
 *     summary?()   -> [{ title, image?, priceMoney, remove?() }]   // its "Your Build" lines;
 *                                                // remove() only where the line is optional
 *   }
 *
 * Providers only *report* invalid elements — this layer owns the whole UI
 * response: .highlight-required/.highlight-complete label icons, the error/success
 * banner state, the header-offset scroll + text-field focus, and the guided
 * scroll-to-next flow (see the interaction layer at the bottom of this file).
 *
 * Providers fire a bubbling `on:addons:change` whenever a selection changes; this
 * layer listens and repaints the total. See context/builder.md.
 */
window.EmbarkAddons = window.EmbarkAddons || (function () {
  const providers = new Set();
  const live = () => [...providers].filter((p) => !p.el || p.el.isConnected);
  let highlighted = new Set();
  const completed = new Set();

  const WRAPPERS = '[data-option-field], .required-group, [data-kit-step]';

  /** Resolve an invalid element (often a visually-hidden radio) to the wrapper that owns its label/heading. */
  function visibleAnchor(el) {
    return el.closest(WRAPPERS) || el;
  }

  /**
   * Keep the form error banner in step with re-validation: flip it to a green
   * success note once every flagged field is fixed, and back to an error if one
   * breaks again. Only touches a banner already shown by a blocked ATC attempt.
   */
  function reflectBanner(firstInvalid, message) {
    const banner = document.querySelector('.js-product-form-main')
      ?.closest('product-form')?.querySelector('.js-form-error');
    if (!banner) return;
    if (firstInvalid) {
      if (banner.classList.contains('bg-success-bg')) {
        banner.classList.remove('bg-success-bg', 'text-success-text');
        banner.classList.add('bg-error-bg', 'text-error-text');
        if (!banner.hidden) banner.textContent = '✕  ' + message;
      }
    } else if (!banner.hidden && completed.size) {
      banner.classList.remove('bg-error-bg', 'text-error-text');
      banner.classList.add('bg-success-bg', 'text-success-text');
      banner.textContent = '✓  All required fields are complete.';
    }
  }

  /**
   * Clearance for what overlays the viewport top AT THE DESTINATION of a
   * scroll: the sticky header's height. Not its current bottom edge — that
   * counts whatever sits above it (announcement bar) which scrolls away en
   * route and would land the anchor with a fat gap. And not --header-end —
   * the header's JS only re-measures that on init and show/hide transitions
   * (its scroll handler is disabled), so it goes stale.
   */
  function headerClearance() {
    const headerEl = document.querySelector('.cc-header');
    if (headerEl) {
      const position = getComputedStyle(headerEl).position;
      if (position === 'sticky' || position === 'fixed') {
        return headerEl.getBoundingClientRect().height + 12;
      }
    }
    const varEnd = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-end')) || 0;
    return Math.max(varEnd, 36) + 12;
  }

  /**
   * Scroll an anchor into view just below the sticky header (headerClearance
   * above) with a small cushion, then focus its
   * text field so the user can type right away. Buttons/radios/selects are
   * never focused: iOS ignores preventScroll on focus (dragging controls below
   * long descriptions into view) and can auto-open a focused select's picker.
   *
   * On DESKTOP, an anchor that is already comfortably in view is left alone —
   * realigning the page under the user is the annoyance, not the help. "In
   * view" = the anchor's top sits below the header line and enough of it (its
   * first control's worth, capped at 200px) fits above the fold. On MOBILE the
   * advance always scrolls: small screens want the next thing brought to the
   * top, and "already visible" is too easy to half-satisfy there.
   *
   * Entering a feature lands on its heading: when the target is the FIRST
   * field wrapper inside a [data-scroll-section] (product options, required
   * addons, the kit configurator), the section becomes the anchor so its
   * heading and intro aren't scrolled past. Later fields in the same section
   * anchor on themselves.
   */
  function scrollToAnchor(el) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // In the quick add drawer the form scrolls inside the drawer, not with the page.
    const scroller = el.closest('[data-scroll-container]');
    const view = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const offset = scroller ? view.top + 12 : headerClearance();

    let anchor = el;
    const section = el.closest('[data-scroll-section]');
    if (section && section.querySelector(WRAPPERS) === el) anchor = section;

    const rect = anchor.getBoundingClientRect();
    const needed = Math.min(rect.height, 200);
    const desktop = window.theme?.mediaMatches?.md ?? window.matchMedia('(min-width: 769px)').matches;
    // The form's sticky Add to Cart bar covers the bottom of the viewport.
    const bar = el.closest('form')?.querySelector('.add-to-cart-wrapper--sticky');
    const fold = view.bottom - (bar ? bar.getBoundingClientRect().height : 0);
    const inView = desktop && rect.top >= offset - 8 && rect.top + needed <= fold;
    if (!inView) {
      (scroller || window).scrollTo({
        top: rect.top + (scroller ? scroller.scrollTop : window.scrollY) - offset,
        behavior: reduced ? 'auto' : 'smooth'
      });
    }

    const focusEl = el.matches('input[type="text"], textarea')
      ? el
      : el.querySelector('input[type="text"], textarea');
    focusEl?.focus({ preventScroll: true });
  }

  /** Base product unit price in cents, read live from the DOM. */
  function basePriceCents() {
    const priceEl = document.querySelector('.product-info__price .price__current');
    if (!priceEl) return 0;
    const dollars = parseFloat(priceEl.textContent.replace(/[^0-9.]/g, ''));
    return Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
  }

  /**
   * The main product's per-unit dropship fee in cents: the checked Ships From option's
   * data-dropship-fee — the same cents product-form.js stamps and the Cart Transform charges
   * (0 when the option carries no fee or there's no picker).
   */
  function mainDropshipFeeCents() {
    const radio = document.querySelector('.js-product-form-main .dealer-dropship input[type="radio"]:checked');
    const cents = parseInt(radio?.dataset.dropshipFee, 10);
    return Number.isFinite(cents) && cents > 0 ? cents : 0;
  }

  /** Main product quantity (defaults to 1). */
  function mainQty() {
    const qtyEl = document.querySelector('.js-product-form input[name="quantity"]');
    const qty = qtyEl ? parseInt(qtyEl.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  /** Shop currency code (falls back to CAD). */
  function currencyCode() {
    return (window.theme && window.theme.settings && window.theme.settings.currencyCode) || 'CAD';
  }

  /**
   * Format cents as money, e.g. "$1,234.56". Addon prices in the form leave the currency code
   * to the main product price and the cart; `withCurrency` adds it ("$1,234.56 CAD") — the Add
   * to Cart total does.
   */
  function formatMoney(cents, withCurrency = false) {
    const n = Number.isFinite(cents) ? cents : 0;
    const amount = '$' + (n / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return withCurrency ? `${amount} ${currencyCode()}` : amount;
  }

  // Stock badge tones (.stock-badge--*, embark-builder-stock.liquid) and estimate icons.
  const STOCK_TONES = ['ok', 'special', 'unknown', 'alert', 'empty'];
  // The badge has one more: an unsellable pick ("Unavailable").
  const BADGE_TONES = [...STOCK_TONES, 'unavailable'];
  const ESTIMATE_KINDS = ['po', 'lead', 'contact', 'pick', 'ship'];
  // Option-value dot per tone, best first: the dot is the colour of the badge the best variant
  // behind that value would get.
  const DOT_CLASSES = { ok: 'has-stock', special: 'has-special', unknown: 'has-unknown', alert: 'has-alert', empty: 'has-none' };

  /**
   * Paint one builder card (.builder-opt, embark-builder.css) — kit option cards and required
   * addons share the markup
   * (snippets/kit-addons-step.liquid, snippets/embark-required-variant-picker.liquid):
   *   - option values: always selectable; .is-unavailable (struck through) when no sellable
   *     variant carries the value and fits the other picks, and the stock dot — the best tone
   *     among the sellable variants fitting the picks: green (in stock / available) > yellow
   *     (restock copy) > black (stock unknown) > red (discontinued / temporarily out but
   *     still buyable) > grey (nothing buyable with these picks). Dots paint only where
   *     Liquid rendered them (embark-stock-status-gate); outside that audience the island
   *     carries no tones either, so the badge and estimate stay empty for a sellable pick.
   *   - legend readouts ("Select your Color: Red"), for visible groups
   *   - card image, price, stock badge and estimate line: the resolved variant's; until it
   *     resolves, the product image, the "From" price, and over the variants fitting the picks
   *     so far "In stock" when every one is, "Available" when some are, else no badge. A
   *     resolved-but-unsellable variant reads "Unavailable" for every viewer, with "Please
   *     select another option" (a card always has one: products with nothing sellable never
   *     render their options).
   * `variants` is the card's island; `chosen` the picked value per dimension (null = open).
   */
  function paintCard(card, { groups, variants, chosen, variant }) {
    const fitsPicks = (v, skip) => chosen.every((sel, j) => j === skip || sel === null || v.options[j] === sel);
    const dotted = !!card.querySelector('.opt-stock-dot');

    groups.forEach((group, i) => {
      const readout = group.closest('[hidden]') ? null : group.querySelector('[data-option-readout]');
      if (readout) readout.textContent = chosen[i] ? `: ${chosen[i]}` : '';

      group.querySelectorAll('input.js-option').forEach((input) => {
        const value = input.value;
        const fits = (v) => v.sellable && v.options[i] === value && fitsPicks(v, i);
        const fitting = variants.filter(fits);
        input.classList.toggle('is-unavailable', !fitting.length);
        if (!dotted) return;
        const dot = STOCK_TONES.find((t) => fitting.some((v) => v.stockTone === t)) || 'empty';
        STOCK_TONES.forEach((t) => input.classList.toggle(DOT_CLASSES[t], t === dot));
      });
    });

    const imgEl = card.querySelector('.builder-opt__img img');
    if (imgEl) {
      const src = (variant && variant.image) || imgEl.dataset.defaultSrc;
      if (src && imgEl.getAttribute('src') !== src) imgEl.src = src;
    }

    const priceEl = card.querySelector('[data-builder-price]');
    if (priceEl) priceEl.textContent = variant ? variant.priceMoney || '' : priceEl.dataset.defaultMoney || '';

    const stockEl = card.querySelector('[data-builder-stock]');
    if (stockEl) {
      let tone = '';
      let label = '';
      if (variant) {
        tone = variant.sellable ? variant.stockTone || '' : 'unavailable';
        label = variant.sellable ? variant.stockLabel || '' : 'Unavailable';
      } else {
        const fitting = variants.filter((v) => fitsPicks(v, -1));
        if (fitting.length && fitting.every((v) => v.sellable && v.stockLabel === 'In stock')) {
          tone = 'ok';
          label = 'In stock';
        } else if (fitting.some((v) => v.sellable && v.stockTone === 'ok')) {
          tone = 'ok';
          label = 'Available';
        }
      }
      stockEl.textContent = label;
      BADGE_TONES.forEach((t) => stockEl.classList.toggle(`stock-badge--${t}`, t === tone));
    }

    const estimateEl = card.querySelector('[data-builder-estimate]');
    if (estimateEl) {
      let kind = '';
      let text = '';
      if (variant && variant.sellable) {
        kind = variant.stockEstimateKind || '';
        text = kind ? variant.stockEstimate || '' : '';
      } else if (variant) {
        kind = 'pick';
        text = 'Please select another option';
      }
      estimateEl.textContent = text;
      ESTIMATE_KINDS.forEach((k) => estimateEl.classList.toggle(`builder-opt__estimate--${k}`, k === kind));
    }
  }

  /**
   * Line item properties a builder variant's stock status adds to its own cart line: a Stock
   * unknown pick carries its follow-up note; a dealer's market dropship source carries the same
   * stamp the Ships From picker sends (product-form.js) — the Cart Transform charges the fee.
   * A native bundle carries the `_dropship_bundle_*` keys instead: its fee rides a separate
   * line (dropshipFeeLines), as on a bundle's own page.
   */
  function stockProperties(variant) {
    if (!variant) return {};
    if (variant.stockTone === 'unknown') return { 'Stock Unknown': variant.stockEstimate || '' };
    if (!variant.dropshipInfo) return {};
    const prefix = variant.dropshipBundle ? '_dropship_bundle' : '_dropship';
    const props = { [`${prefix}_info`]: variant.dropshipInfo };
    if (variant.dropshipLocation) {
      props[`${prefix}_location`] = variant.dropshipLocation;
      if (!variant.dropshipBundle && variant.dropshipFeeCents > 0) props._dropship_fee = String(variant.dropshipFeeCents);
    }
    return props;
  }

  /**
   * A native bundle's dropship fee as its own cart line — the bundle product page's fee line
   * (product-form.js): the shop dummy fee variant, married to the bundle line by parent
   * variant + location, priced by the Cart Transform from `_dropship_bundle_fee`, quantity kept
   * in step by <dropship-fee-sync>. Return it BEFORE the bundle line in lineItems(), so the
   * bundle (added last = newest) renders above it. Empty for anything else.
   */
  function dropshipFeeLines(variant, bundleTitle) {
    if (!variant || !variant.dropshipBundle || !variant.dropshipLocation) return [];
    if (!(variant.dropshipFeeCents > 0) || !variant.dropshipFeeVariant) return [];
    const parentId = String(variant.id).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    return [{
      id: variant.dropshipFeeVariant,
      quantity: 1,
      properties: {
        ...(bundleTitle ? { 'Bundled with': bundleTitle } : {}),
        _dropship_bundle_location: variant.dropshipLocation,
        _dropship_bundle_info: variant.dropshipInfo || '',
        _dropship_bundle_parent_variant: parentId,
        _dropship_bundle_fee: String(variant.dropshipFeeCents)
      }
    }];
  }

  /** A builder variant's line price with its dropship fee, in cents — for totals and Your Build. */
  function lineCents(variant) {
    return variant ? (variant.priceCents || 0) + (variant.dropshipFeeCents || 0) : 0;
  }

  /**
   * "Your Build" (snippets/embark-builder-summary.liquid): the main product's line, then one
   * line per selection reported by the providers' summary(), in page order. The element shows
   * whenever something is in the build beyond the product itself ([data-always] keeps it up
   * regardless — kits). A line with remove() gets a ✕ that calls it.
   */
  let summaryLines = [];

  function reflectSummary() {
    const el = document.querySelector('.js-product-form-main [data-builder-summary]');
    const linesEl = el && el.querySelector('[data-builder-lines]');
    const baseRow = linesEl && linesEl.firstElementChild;
    if (!baseRow) return;

    summaryLines = live()
      .filter((p) => p.summary)
      .sort((a, b) => (a.el && b.el && (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_PRECEDING) ? 1 : -1))
      .flatMap((p) => p.summary() || []);

    const qty = mainQty();
    const basePrice = baseRow.querySelector('[data-builder-base-price]');
    if (basePrice && basePriceCents()) basePrice.textContent = formatMoney((basePriceCents() + mainDropshipFeeCents()) * qty);
    const baseQty = baseRow.querySelector('[data-builder-base-qty]');
    if (baseQty) baseQty.textContent = qty > 1 ? ` × ${qty}` : '';

    // Added lines reuse the base line's thumb classes (image-blend follows the theme setting).
    const thumbClass = baseRow.querySelector('.builder-summary__thumb')?.className || 'builder-summary__thumb';
    const fragment = document.createDocumentFragment();
    summaryLines.forEach((line, index) => {
      const row = document.createElement('div');
      row.className = 'builder-summary__line';

      const thumb = document.createElement('span');
      thumb.className = thumbClass;
      if (line.image) {
        const img = document.createElement('img');
        img.src = line.image;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
      }

      const name = document.createElement('div');
      name.className = 'builder-summary__name';
      name.textContent = line.title;

      const price = document.createElement('div');
      price.className = 'builder-summary__price font-bold';
      price.textContent = line.priceMoney || '';

      row.append(thumb, name, price);

      if (line.remove) {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'builder-summary__remove';
        remove.dataset.builderRemove = index;
        remove.setAttribute('aria-label', `Remove ${line.title}`);
        remove.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M7 7l10 10M17 7L7 17"/></svg>';
        row.appendChild(remove);
      }
      fragment.appendChild(row);
    });

    while (linesEl.lastChild && linesEl.lastChild !== baseRow) linesEl.removeChild(linesEl.lastChild);
    linesEl.appendChild(fragment);

    const count = summaryLines.length + 1;
    const countEl = el.querySelector('[data-builder-count]');
    if (countEl) countEl.textContent = `· ${count} ${count === 1 ? 'item' : 'items'}`;
    el.hidden = !(el.hasAttribute('data-always') || summaryLines.length);
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-builder-remove]');
    const line = btn && summaryLines[Number(btn.dataset.builderRemove)];
    if (line && line.remove) line.remove();
  });

  const api = {
    register(provider) {
      providers.add(provider);
      api.reflect();
    },

    unregister(provider) {
      providers.delete(provider);
      api.reflect();
    },

    /** Repaint everything the layer displays: the Add to Cart total and Your Build. */
    reflect() {
      api.reflectTotal();
      reflectSummary();
    },

    reflectSummary,

    hasProviders() {
      return live().length > 0;
    },

    /** Dispatch the shared change signal so the total repaints. Providers call this on selection change. */
    notifyChange() {
      document.dispatchEvent(new CustomEvent('on:addons:change', { bubbles: true }));
    },

    /** Shared money formatter (cents -> "$X.XX", or "$X.XX <CUR>" with withCurrency). Single source of truth for addon pricing display. */
    formatMoney,

    /** Shared addon card painter (kit options, required addons) — see paintCard above. */
    paintCard,

    stockProperties,

    dropshipFeeLines,

    lineCents,

    /**
     * Gate: aggregate every provider's validate(). Owns the .highlight-required /
     * .highlight-complete classes — invalid elements resolve to wrapper anchors,
     * sorted in DOM order, so firstInvalid is the first missing field on the page;
     * anchors that were flagged and then fixed flip to .highlight-complete.
     */
    validateAll({ scrollToNext = false } = {}) {
      // Dynamic-content swaps (variant/product changes) replace wrappers wholesale;
      // drop detached nodes so they aren't miscounted as "fixed" below.
      highlighted.forEach((el) => { if (!el.isConnected) highlighted.delete(el); });
      completed.forEach((el) => { if (!el.isConnected) completed.delete(el); });

      const anchors = new Map();
      live().forEach((p) => {
        const result = p.validate && p.validate();
        if (!result || result.valid) return;
        const invalid = (result.invalid && result.invalid.length ? result.invalid : [p.el]).filter(Boolean);
        invalid.forEach((el) => {
          const anchor = visibleAnchor(el);
          if (!anchors.has(anchor)) anchors.set(anchor, result.message || null);
        });
      });

      const sorted = [...anchors.keys()].sort((a, b) => (
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      ));

      let fixedCount = 0;
      highlighted.forEach((el) => {
        if (!anchors.has(el)) {
          el.classList.remove('highlight-required');
          el.classList.add('highlight-complete');
          completed.add(el);
          fixedCount += 1;
        }
      });
      completed.forEach((el) => {
        if (anchors.has(el)) {
          el.classList.remove('highlight-complete');
          completed.delete(el);
        }
      });
      sorted.forEach((el) => el.classList.add('highlight-required'));
      highlighted = new Set(sorted);

      const firstInvalid = sorted[0] || null;
      const message = firstInvalid
        ? (anchors.get(firstInvalid) || 'Please complete the highlighted required fields.')
        : null;
      if (scrollToNext && fixedCount && firstInvalid) scrollToAnchor(firstInvalid);
      reflectBanner(firstInvalid, message);
      return { valid: !firstInvalid, firstInvalid, message };
    },

    /** Shared invalid-field scroll (used by product-form's blocked-submit path too). */
    scrollToAnchor,

    /**
     * Single source of truth for the displayed total: (base + the main product's Ships From
     * fee)×qty + every subtotal(), shown in the Add to Cart button ("Add to cart · $1,234.00").
     * Only the button's
     * normal state carries it — sold out / pick-an-option keep the theme's text.
     * variant-picker.js resets the text on every variant change, then fires
     * on:variant:change, which lands back here.
     */
    reflectTotal() {
      const btn = document.querySelector('.js-product-form-main [name="add"][data-total-in-button]');
      if (!btn) return;
      const label = btn.dataset.addToCartText || '';
      if (btn.disabled || !label || !btn.textContent.trim().startsWith(label)) return;

      const qty = mainQty();
      let addonCents = 0;
      live().forEach((p) => {
        if (p.subtotal) addonCents += p.subtotal() || 0;
      });
      const totalCents = (basePriceCents() + mainDropshipFeeCents()) * qty + addonCents;

      btn.textContent = `${label} · ${formatMoney(totalCents, true)}`;
    },

    /** ONE combined POST for all providers' extra line items. */
    async addToCart({ sections, sections_url } = {}) {
      const items = live().flatMap((p) => (p.lineItems ? p.lineItems() : []) || []);
      if (!items.length) return { ok: true, sections: null };

      const url = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items, sections, sections_url })
        });
        const data = await res.json();
        if (!res.ok) {
          return { ok: false, error: (data && (data.description || data.message)) || 'Failed to add items to cart.', sections: null };
        }
        return { ok: true, sections: data.sections || null };
      } catch (e) {
        return { ok: false, error: e.message, sections: null };
      }
    }
  };

  return api;
})();

// Repaint the shared total and Your Build on any selection change, variant change, or qty
// change. (Feature events like on:required-variant:change are translated to on:addons:change
// by their owning module — the layer only listens to generic triggers.)
document.addEventListener('on:addons:change', () => window.EmbarkAddons.reflect());
document.addEventListener('on:variant:change', () => window.EmbarkAddons.reflect());

// Recompute once after all deferred component scripts have upgraded their elements,
// so selections resolved at init (e.g. a pre-checked single-variant required addon)
// are reflected in the total even though they never fire a change event.
if (document.readyState === 'complete') {
  window.EmbarkAddons.reflect();
} else {
  document.addEventListener('DOMContentLoaded', () => window.EmbarkAddons.reflect(), { once: true });
}
document.addEventListener('input', (e) => {
  if (e.target && e.target.matches && e.target.matches('.js-product-form input[name="quantity"]')) {
    window.EmbarkAddons.reflect();
  }
});
// A different Ships From option changes the main product's dropship fee
document.addEventListener('change', (e) => {
  if (e.target && e.target.matches && e.target.matches('.js-product-form-main .dealer-dropship input[type="radio"]')) {
    window.EmbarkAddons.reflect();
  }
});

// Interaction layer: re-validation of flagged fields (X -> checkmark), guided
// scroll-to-next, and Enter handling inside option/addon field wrappers.
(function () {
  const FIELD_WRAPPERS = '[data-option-field], .required-group';
  const ALL_WRAPPERS = `${FIELD_WRAPPERS}, [data-kit-step]`;
  const FLAGGED = '.highlight-required, .highlight-complete';

  // Deferred so field-level handlers that set custom validity (e.g. the VIN
  // input) run before the state is recomputed.
  let revalidateQueued = false;
  let advanceQueued = false;
  function queueRevalidate(advance) {
    advanceQueued = advanceQueued || !!advance;
    if (revalidateQueued) return;
    revalidateQueued = true;
    setTimeout(() => {
      revalidateQueued = false;
      const scrollToNext = advanceQueued;
      advanceQueued = false;
      window.EmbarkAddons.validateAll({ scrollToNext });
    });
  }

  // A field wrapper counts as answered once it has a selection or content.
  function fieldAnswered(wrapper) {
    if (wrapper.matches('[data-kit-step]')) {
      return !!wrapper.querySelector('.builder-opt--selected, .kit-skip--selected');
    }
    if (wrapper.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) return true;
    return [...wrapper.querySelectorAll('input[type="text"], input[type="file"], textarea, select')]
      .some((el) => el.value && el.checkValidity());
  }

  // A checkbox in a multi-checkbox group shouldn't auto-advance — the user may
  // want to select more than one.
  function isMultiCheckbox(t, wrapper) {
    return !!wrapper
      && t.matches('input[type="checkbox"]')
      && wrapper.querySelectorAll('input[type="checkbox"]').length > 1;
  }

  // Scroll to the next unanswered field wrapper after the given one, if any.
  function advanceFrom(wrapper) {
    const wrappers = [...document.querySelectorAll(ALL_WRAPPERS)];
    const start = wrappers.indexOf(wrapper);
    if (start === -1) return;
    const next = wrappers.slice(start + 1).find((w) => !fieldAnswered(w));
    if (next) window.EmbarkAddons.scrollToAnchor(next);
  }

  ['input', 'change'].forEach((type) => {
    document.addEventListener(type, (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const isTextField = t.matches && t.matches('input[type="text"], textarea');

      // Re-validate when the user interacts with a flagged field so its X flips
      // to a checkmark once fixed (capture phase so it fires regardless of
      // stopPropagation in feature components).
      const anchor = t.closest(FLAGGED);
      if (anchor) {
        // Text fields with content in progress hide the X (empty again brings it
        // back). VIN keeps its inline requirement message visible while typing.
        if (isTextField && !t.hasAttribute('data-vin')) {
          anchor.classList.toggle('is-typing', t.value.trim().length > 0);
        }
        // Fixing a field via a selection auto-advances to the next flagged one;
        // text fields wait for Enter (keydown listener below).
        queueRevalidate(!isTextField && !isMultiCheckbox(t, t.closest(FIELD_WRAPPERS)));
        return;
      }

      // Guided flow before any ATC attempt: completing a field via a selection
      // scrolls to the next unanswered field.
      const wrapper = t.closest(FIELD_WRAPPERS);
      if (!wrapper) return;
      const completedSelection = e.type === 'change'
        && t.matches('input[type="radio"], input[type="checkbox"], select, input[type="file"]')
        && !isMultiCheckbox(t, wrapper);
      if (completedSelection) {
        setTimeout(() => {
          if (fieldAnswered(wrapper)) advanceFrom(wrapper);
        });
      }
    }, true);
  });

  // Enter in an option text field advances instead of submitting the form.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const t = e.target;
    if (!t.matches || !t.matches('input[type="text"]')) return;
    const wrapper = t.closest(FIELD_WRAPPERS);
    if (!wrapper) return;
    e.preventDefault();
    if (t.closest(FLAGGED)) {
      // Re-validate now and move on to the first remaining flagged field —
      // the field may have already flipped to complete while typing.
      const result = window.EmbarkAddons.validateAll();
      if (result.firstInvalid) window.EmbarkAddons.scrollToAnchor(result.firstInvalid);
    } else if (fieldAnswered(wrapper)) {
      advanceFrom(wrapper);
    }
  }, true);

  // Click-driven features (kit steps) fire on:addons:change instead of
  // input/change. Only re-validate once fields have been flagged by an ATC attempt.
  document.addEventListener('on:addons:change', () => {
    if (document.querySelector(FLAGGED)) queueRevalidate(true);
  });
})();

// Auto-add bundled products ride the main item (_auto_add_variant_ids, expanded by
// the Cart Transform app) and are intentionally NOT included in the button's total.
// Their props are still submitted via the [data-auto-add-props] carrier (product-form.js).
